// The Guardrails & Verification tab (ADR 0039; the guide's §25–27): a summary, then the guardrails — the topic
// (scope), facts (grounding: did the agent look at the data, checked by code) and review (verify: does the answer
// match the data, a second model) — with what a failed one does (on_fail: refuse with the refusal text, warn, or warn
// and mark the run for a person), then verification: the checks of the agent's harness, which never change an
// answer and mark the run. Honesty (disclose) is a line of the instructions only, shown in Context. A project agent's
// fields are saved with the agent (one commit); a platform agent's are read-only here. The tool-call limit is
// Budgets', so `max_tool_calls` is kept as it is.
import type { Agent } from '@cortex/sdk-agents-client';
import { cn } from '@cortex/ui';
import { Loader2, RotateCcw, Save } from 'lucide-react';
import * as React from 'react';
import { HarnessSections } from './HarnessTab.tsx';
import { useAgentsHost, useAgentsQuery, useInvalidate, useAgentsKey } from './host.tsx';
import { guardDraftOf, guardPatch, guardProblems, type GuardDraft } from './model.ts';
import type { AgentTabContext } from './tabs.tsx';
import { btnGhost, btnPrimary, input, messageOf, SwitchTrack } from './ui.tsx';

const DEFAULT_REFUSAL = 'I cannot give a verified answer to that from the data available to me.';

const Num = ({ n }: { n: number }) => (
  <span className="shrink-0 text-xs font-medium text-page-muted">{n}</span>
);

/** A guardrail that is a line of the agent's instructions only: nothing checks it (the guide's §35). */
const InstructionOnly = ({ long }: { long?: boolean }) => (
  <span
    className="rounded bg-violet-50 px-1.5 py-0.5 text-[11px] font-medium text-violet-700 dark:bg-violet-950/40 dark:text-violet-300"
    data-testid="guardrails-instruction-only"
  >
    {long ? 'instruction only — not checked' : 'instruction only'}
  </span>
);

function Card({
  n,
  title,
  sub,
  badge,
  testId,
  children,
}: {
  n?: number;
  title: string;
  sub?: string;
  badge?: React.ReactNode;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-md border border-card-border bg-card-bg p-4 scroll-mt-4" data-testid={testId}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {n ? <Num n={n} /> : null}
        <h3 className="m-0 text-[15px] font-semibold text-page-title">{title}</h3>
        {sub ? <span className="text-xs text-page-muted">— {sub}</span> : null}
        {badge}
      </div>
      {children}
    </section>
  );
}

function Row({
  n,
  label,
  ok,
  icon,
  href,
  fix,
  children,
}: {
  n: number;
  label: string;
  ok: boolean;
  icon: string;
  href?: string;
  fix?: string;
  children: React.ReactNode;
}) {
  return (
    <li
      className="flex items-center gap-2 border-t border-divider first:border-t-0 px-1 py-1.5 text-sm min-w-0"
      data-testid={`guardrails-summary-${n}`}
      data-ok={String(ok)}
    >
      <span className="w-4 text-center text-xs">
        {ok ? (
          <span
            className={cn('text-emerald-600 dark:text-emerald-400', icon === '●' ? 'text-[9px]' : 'text-xs')}
            title={icon === '●' ? 'Fine' : 'An instruction: nothing checks it'}
          >
            {icon}
          </span>
        ) : (
          <span className="text-[9px] text-amber-500" title="Needs a look">
            ●
          </span>
        )}
      </span>
      <span className="flex w-28 shrink-0 items-center gap-1.5 font-semibold text-page-title">
        <Num n={n} /> {label}
      </span>
      <span className="min-w-0 truncate text-page-text">{children}</span>
      {!ok && href && fix ? (
        <a
          href={`#${href}`}
          className="ml-auto shrink-0 text-xs text-nav-active no-underline hover:underline"
          onClick={(e) => {
            e.preventDefault();
            document
              .querySelector(`[data-testid="${href}"]`)
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        >
          {fix} ›
        </a>
      ) : null}
    </li>
  );
}

function Switch({
  on,
  label,
  disabled,
  onChange,
  testId,
}: {
  on: boolean;
  label: React.ReactNode;
  disabled: boolean;
  onChange(on: boolean): void;
  testId: string;
}) {
  return (
    <label
      className={cn('flex items-center gap-3 py-1 text-sm', disabled ? '' : 'cursor-pointer')}
      data-testid={testId}
      data-on={String(on)}
    >
      <input
        type="checkbox"
        role="switch"
        className="sr-only"
        checked={on}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <SwitchTrack on={on} dim={disabled} />
      <span className="text-page-text">{label}</span>
    </label>
  );
}

/** The agent as its PUT takes it, with these guardrails (everything else as it is). */
function saveBody(a: Agent, guardrails: Record<string, unknown> | null) {
  return {
    title: a.title,
    icon: a.icon ?? null,
    description: a.description ?? null,
    persona: a.persona,
    modelRole: a.modelRole,
    guardrails,
    tools: a.tools.map((t) => ({
      name: t.name,
      source: t.source,
      operationId: t.operationId,
      ...(t.source === 'app' ? { app: t.app } : {}),
      description: t.description ?? null,
    })),
    covers: a.covers,
  };
}

/** A project agent's guardrails as a draft: what is saved, the edit, its problems and the save (one commit). */
function useGuardrails(agent: AgentTabContext['agent']) {
  const host = useAgentsHost();
  const k = useAgentsKey();
  const invalidate = useInvalidate();
  const projectAgent = agent.kind === 'project';
  const full = useAgentsQuery(k('agent', agent.key), () => host.client.get(agent.key), {
    enabled: projectAgent,
  });
  const [draft, setDraft] = React.useState<GuardDraft | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [note, setNote] = React.useState('');
  const savedDraft = guardDraftOf(
    (projectAgent ? (full.data?.guardrails ?? null) : agent.guardrails) as Record<string, unknown> | null,
  );
  const g = draft ?? savedDraft;
  const open = projectAgent && !!full.data && !busy;
  const set = (patch: Partial<GuardDraft>) => {
    setNote('');
    setDraft((d) => ({ ...(d ?? savedDraft), ...patch }));
  };
  // A run is grounded by any successful tool call (guardrails.ts): an agent without a single tool that reads
  // can never ground an answer with figures.
  const readsAny = (full.data?.tools ?? []).some(
    (t) => (t as { operation?: { access?: string } | null }).operation?.access === 'read',
  );
  const changed = draft
    ? JSON.stringify(guardPatch(draft)) !== JSON.stringify(guardPatch(savedDraft))
    : false;
  const problems = draft ? guardProblems(draft) : [];
  const save = async () => {
    if (!draft || !full.data) return;
    setBusy(true);
    setError('');
    try {
      await host.client.save(agent.key, saveBody(full.data, guardPatch(draft)) as never);
      setDraft(null);
      setNote(`Saved as one change. The next run uses it.`);
      invalidate([k()]);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const loading = projectAgent && full.loading && !full.data;
  const failed = projectAgent ? full.error : '';
  const footer = (
    <>
      {problems.length ? <div className="text-sm text-red-600">{problems.join(' ')}</div> : null}
      {error ? (
        <div className="text-sm text-red-600" data-testid="guardrails-error">
          {error}
        </div>
      ) : null}
      {note ? (
        <div className="text-sm text-page-muted" data-testid="guardrails-note">
          {note}
        </div>
      ) : null}
      {changed ? (
        <div className="flex flex-wrap items-center gap-2 sticky bottom-0 bg-page-bg py-2 border-t border-card-border">
          <span className="text-xs text-page-muted" data-testid="guardrails-unsaved">
            Changes not saved
          </span>
          <span className="flex-1" />
          <button
            type="button"
            className={btnGhost}
            data-testid="guardrails-discard"
            disabled={busy}
            onClick={() => setDraft(null)}
          >
            <RotateCcw size={14} /> Discard
          </button>
          <button
            type="button"
            className={btnPrimary}
            data-testid="guardrails-save"
            disabled={busy || problems.length > 0}
            onClick={() => void save()}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        </div>
      ) : null}
    </>
  );
  return { projectAgent, full, g, open, set, readsAny, changed, loading, failed, footer };
}

/** Honesty (Context): a line of the agent's instructions — nothing checks that the agent follows it (§35). */
export function HonestyCard({ agent }: AgentTabContext) {
  const { g, open, set, loading, footer } = useGuardrails(agent);
  if (loading) return null;
  return (
    <section className="rounded-md border border-card-border bg-card-bg p-4" data-testid="context-honesty">
      <Switch
        on={g.disclose}
        disabled={!open}
        testId="guardrails-disclose"
        label={
          <>
            Honesty: say what it could not check — never fill a gap with a guess <InstructionOnly long />
          </>
        }
        onChange={(on) => set({ disclose: on })}
      />
      <div className="ml-12 text-xs text-page-muted">
        A line of the instructions: nothing checks that the agent follows it.
      </div>
      {footer}
    </section>
  );
}

/** A group of the tab: its name and what it does, in one line. */
const Group = ({ title, sub, testId }: { title: string; sub: string; testId: string }) => (
  <div className="mt-2 flex flex-wrap items-baseline gap-2" data-testid={testId}>
    <h3 className="m-0 text-[15px] font-bold text-page-title">{title}</h3>
    <span className="text-xs text-page-muted">— {sub}</span>
  </div>
);

export function GuardrailsTab(ctx: AgentTabContext) {
  const { agent } = ctx;
  const host = useAgentsHost();
  const k = useAgentsKey();
  const { projectAgent, full, g, open, set, readsAny, changed, loading, failed, footer } =
    useGuardrails(agent);
  // The Verification line of the summary counts the checks of the agent's harness (the same query as its section).
  const harness = useAgentsQuery(k('harness', agent.subject), () => host.client.harness(agent.subject));
  const eff = (harness.data?.effective.harness ?? {}) as {
    verification?: { checks?: unknown[]; end_state?: unknown[] };
  };
  const nChecks = (eff.verification?.checks?.length ?? 0) + (eff.verification?.end_state?.length ?? 0);
  const passing = [g.grounding === 'tools' ? 'Facts' : '', g.verify ? 'Review' : ''].filter(Boolean);

  if (loading)
    return (
      <div className="flex items-center gap-2 text-sm text-page-muted">
        <Loader2 size={14} className="animate-spin" /> Reading the guardrails…
      </div>
    );
  if (failed) return <div className="text-sm text-red-600">{failed}</div>;

  const topic = g.scope.trim();
  const topicOk = !!topic && g.verify;
  return (
    <div className="flex flex-col gap-3" data-testid="guardrails-tab">
      <div className="text-sm text-page-muted" data-testid="guardrails-rule">
        Guardrails can stop or flag the answer. Verification never does: it checks whether the work was really
        done.
      </div>
      <Card
        n={1}
        title="Summary"
        sub="how its answers are guarded and checked now"
        testId="guardrails-summary"
      >
        <div className="-mt-2 mb-2 text-xs text-page-muted" data-testid="guardrails-summary-lead">
          {changed ? 'Includes unsaved changes.' : ''}
        </div>
        <ul className="m-0 p-0 list-none">
          <Row
            n={2}
            label="Topic"
            ok={topicOk}
            icon="●"
            href={topic ? 'guardrails-section-review' : 'guardrails-section-topic'}
            fix={projectAgent ? (topic ? 'Turn on Review' : 'Set a topic') : undefined}
          >
            {topic ? (
              g.verify ? (
                `only: ${topic}`
              ) : (
                <>
                  only: {topic} — <b>not checked: Review is off</b>
                </>
              )
            ) : (
              <>
                answers <b>any question</b>
              </>
            )}
          </Row>
          <Row
            n={3}
            label="Facts"
            ok={g.grounding === 'tools'}
            icon="●"
            href="guardrails-section-facts"
            fix={projectAgent ? 'Require data' : undefined}
          >
            {g.grounding === 'tools' ? (
              'numbers and names only after the agent looked at the data'
            ) : (
              <>
                may state numbers and names <b>without looking at the data</b>
              </>
            )}
          </Row>
          <Row
            n={4}
            label="Review"
            ok={g.verify}
            icon="●"
            href="guardrails-section-review"
            fix={projectAgent ? 'Turn on' : undefined}
          >
            {g.verify ? 'a second model reads each answer' : 'nobody reviews its answers'}
          </Row>
          <Row
            n={5}
            label="Verification"
            ok={nChecks > 0 || passing.length > 0}
            icon="●"
            href="harness-section-verification"
            fix={projectAgent ? 'Add a check' : undefined}
          >
            {[
              nChecks ? `${nChecks} check${nChecks === 1 ? '' : 's'} after each answer` : '',
              passing.length
                ? `${passing.join(' and ')} count${passing.length === 1 ? 's' : ''} when passed`
                : '',
            ]
              .filter(Boolean)
              .join('; ') || <b>nothing checks the answers</b>}
          </Row>
        </ul>
      </Card>

      <Group
        title="Guardrails"
        sub="can stop or flag the answer before the person sees it"
        testId="guardrails-group"
      />
      <Card n={2} title="Topic" sub="what the agent may answer" testId="guardrails-section-topic">
        <label className="grid gap-1 text-xs text-page-muted">
          It answers only about…
          <textarea
            className={input}
            rows={2}
            data-testid="guardrails-scope"
            disabled={!open}
            placeholder="e.g. designing the application screens of a showcase — empty: any question"
            value={g.scope}
            onChange={(e) => set({ scope: e.target.value })}
          />
        </label>
        <div className="mt-2 text-xs text-page-muted" data-testid="guardrails-topic-note">
          Without Review this is a request to the agent. With Review it is <b>checked</b>.
        </div>
      </Card>

      <Card
        n={3}
        title="Facts"
        sub="Did the agent look at the data? · checked by code"
        testId="guardrails-section-facts"
      >
        <Switch
          on={g.grounding === 'tools'}
          disabled={!open}
          testId="guardrails-grounding"
          label="Numbers and names only after the agent looked at the data"
          onChange={(on) => set({ grounding: on ? 'tools' : 'none' })}
        />
        <div className="ml-12 text-xs text-page-muted">
          On: an answer that states data without a single successful tool call <b>fails</b> (see “When a
          guardrail fails”). Small talk passes.
        </div>
        {projectAgent && full.data && !readsAny ? (
          <div className="ml-12 mt-1 text-xs text-page-muted" data-testid="guardrails-no-read-tool">
            <span className="mr-1 text-[9px] text-amber-500">●</span>
            This agent has no tool that reads data — answers with figures would fail.
          </div>
        ) : null}
      </Card>

      <Card
        n={4}
        title="Review"
        sub="Does the answer match the data? · checked by a second model"
        testId="guardrails-section-review"
      >
        <Switch
          on={g.verify}
          disabled={!open}
          testId="guardrails-verify"
          label={
            <>
              Review each answer <span className="text-xs text-page-muted">(+1 model call: more tokens)</span>
            </>
          }
          onChange={(on) => set({ verify: on })}
        />
        <div className="ml-12 text-xs text-page-muted">
          A second model reads each answer and checks it against the data the agent found and the topic; it
          never edits it.
        </div>
        <div className="ml-12 text-xs text-page-muted">
          If the second model cannot answer (an error), the check is skipped and the answer passes.
        </div>
      </Card>

      <Card title="When a guardrail fails" sub="Facts or Review" testId="guardrails-section-fail">
        <div className="grid gap-2 text-sm">
          {(
            [
              ['refuse', 'refuse with this text:'],
              ['annotate', 'show the answer with a warning'],
              ['human', 'show it with a warning and mark the run for a person'],
            ] as const
          ).map(([v, label]) => (
            <label key={v} className="flex flex-wrap items-center gap-2">
              <input
                type="radio"
                name="guardrails-on-fail"
                className="accent-nav-active"
                data-testid={`guardrails-on-fail-${v}`}
                disabled={!open}
                checked={g.on_fail === v}
                onChange={() => set({ on_fail: v })}
              />
              {label}
              {v === 'refuse' ? (
                <input
                  className={cn(input, 'flex-1 min-w-[240px]')}
                  data-testid="guardrails-refusal"
                  disabled={!open}
                  placeholder={DEFAULT_REFUSAL}
                  value={g.refusal}
                  onChange={(e) => set({ refusal: e.target.value })}
                />
              ) : null}
            </label>
          ))}
        </div>
      </Card>

      <Group
        title="Verification"
        sub="never stops or changes the answer; checks whether the work was really done"
        testId="verification-group"
      />
      <HarnessSections {...ctx} parts={['checks']} />
      {footer}
    </div>
  );
}
